/**
 * EP 订单（与 `GET /api/admin/ep/orders` 字段对齐）。
 */
import { useState } from 'react';
import { Button, Select, Space, Tag, message } from 'antd';
import { useQueryClient } from '@tanstack/react-query';
import { DataTable } from '../../components/DataTable';
import { api, ApiError } from '../../api/client';
import { useAuthStore } from '../../store/auth';

interface EpOrder {
  id: string;
  seller_id: number;
  buyer_id: number | null;
  amount_ep: string;
  price_cny: string;
  payment_methods: string[];
  status: string;
  pay_proof: string | null;
  create_time: string;
  claim_time: string | null;
  pay_time: string | null;
  release_time: string | null;
}

const STATUS_COLORS: Record<string, string> = {
  open: 'blue',
  claimed: 'orange',
  paid: 'gold',
  done: 'green',
  cancelled: 'default',
  disputed: 'red',
  timeout: 'volcano',
};

export interface EpOrdersPageProps {
  /** 争议列表：固定筛选 disputed，展示凭证列，可强制取消（退款）。 */
  mode?: 'all' | 'disputes';
}

export function EpOrdersPage({ mode = 'all' }: EpOrdersPageProps = {}) {
  const [status, setStatus] = useState<string | undefined>(mode === 'disputes' ? 'disputed' : undefined);
  const qc = useQueryClient();
  const hasNode = useAuthStore((s) => s.hasNode);
  const canCancel = hasNode('ep.order.cancel') || hasNode('ep.disputes.resolve');
  const canComplete = hasNode('ep.order.complete');
  const isDisputes = mode === 'disputes';

  async function forceCancel(row: EpOrder) {
    try {
      await api.post(`/api/admin/ep/orders/${row.id}/cancel`);
      message.success(`已取消订单 #${row.id.slice(0, 8)}…`);
      qc.invalidateQueries({ queryKey: ['admin', 'ep-orders'] });
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : '取消失败');
    }
  }

  async function forceComplete(row: EpOrder) {
    try {
      await api.post(`/api/admin/ep/orders/${row.id}/force-complete`);
      message.success(`已强制完成订单 #${row.id.slice(0, 8)}…，EP 已释放给买家`);
      qc.invalidateQueries({ queryKey: ['admin', 'ep-orders'] });
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : '强制完成失败');
    }
  }

  const columns = [
    { title: '订单 ID', dataIndex: 'id', width: 280, ellipsis: true },
    { title: '卖家', dataIndex: 'seller_id', width: 90 },
    {
      title: '买家',
      dataIndex: 'buyer_id',
      width: 90,
      render: (v: number | null) => v ?? '—',
    },
    { title: '数量 (EP)', dataIndex: 'amount_ep', width: 120 },
    { title: '单价 (CNY)', dataIndex: 'price_cny', width: 110 },
    {
      title: '状态',
      dataIndex: 'status',
      width: 100,
      render: (v: string) => <Tag color={STATUS_COLORS[v] ?? 'default'}>{v}</Tag>,
    },
    { title: '创建时间', dataIndex: 'create_time', width: 170 },
    { title: '认领时间', dataIndex: 'claim_time', width: 170, render: (v: string | null) => v ?? '—' },
    { title: '放款时间', dataIndex: 'release_time', width: 170, render: (v: string | null) => v ?? '—' },
    ...(isDisputes
      ? ([
          {
            title: '付款凭证',
            dataIndex: 'pay_proof',
            width: 200,
            ellipsis: true,
            render: (v: string | null) => v ?? '—',
          },
        ] as const)
      : []),
    {
      title: '操作',
      key: 'actions',
      width: 200,
      render: (_: unknown, row: EpOrder) => {
        const cancelable =
          canCancel &&
          (row.status === 'open' || row.status === 'claimed' || row.status === 'disputed');
        const completable = canComplete && row.status === 'paid';
        if (!cancelable && !completable) return '—';
        return (
          <Space>
            {cancelable && (
              <Button size="small" danger onClick={() => forceCancel(row)}>
                {row.status === 'disputed' ? '取消并退款' : '强制取消'}
              </Button>
            )}
            {completable && (
              <Button
                size="small"
                type="primary"
                style={{ background: '#16a34a', borderColor: '#16a34a' }}
                onClick={() => forceComplete(row)}
              >
                强制完成
              </Button>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <div>
      {isDisputes && <h2 style={{ marginTop: 0 }}>EP 争议</h2>}
      <Space style={{ marginBottom: 12 }}>
        {!isDisputes && (
          <Select
            placeholder="订单状态"
            style={{ width: 160 }}
            allowClear
            value={status}
            onChange={setStatus}
            options={Object.keys(STATUS_COLORS).map((s) => ({ value: s, label: s }))}
          />
        )}
      </Space>
      <DataTable<EpOrder>
        queryKey={['admin', 'ep-orders', mode, status ?? '']}
        endpoint="/api/admin/ep/orders"
        extraParams={{ status }}
        columns={columns}
        rowKey="id"
        scroll={{ x: 1600 }}
      />
    </div>
  );
}
